// CONTACTUS DASHBOARD PAGE

import React, { useState } from "react";
import { Menu, X } from "lucide-react";

// Dashboard Layout
import DashboardSidebar from "../../components/dashboard components/DashboardSidebar";
import DashboardHeader from "../../components/dashboard components/DashboardHeader";

// Contact Us Forms
import ContactUsHeroForm from "../../components/dashboard components/forms/ContactUsHeroForm";
import ContactUsEnquiryForm from "../../components/dashboard components/forms/ContactUsEnquiryForm";
import ContactOfficeForm from "../../components/dashboard components/forms/ContactOfficeForm";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ContactUs = () => {
  const [activeSection, setActiveSection] = useState("contact");
  const [activeTab, setActiveTab] = useState("hero");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [tabMenuOpen, setTabMenuOpen] = useState(false);

  // ===================
  // Tabs Config
  // ===================
  const tabs = [
    { id: "hero", label: "Hero Section" },
    { id: "enquiry", label: "Enquiry Form" },
    { id: "office", label: "Office Location" },
  ];

  const activeTabLabel =
    tabs.find((t) => t.id === activeTab)?.label || "Select Section";

  // ===================
  // Render Active Tab
  // ===================
  const renderActiveTab = () => {
    switch (activeTab) {
      case "hero":
        return <ContactUsHeroForm apiBase={`${API_BASE_URL}/contact`} />;
      case "enquiry":
        return <ContactUsEnquiryForm apiBase={`${API_BASE_URL}/contact`} />;
      case "office":
        return <ContactOfficeForm apiBase={`${API_BASE_URL}/contact`} />;
      default:
        return null;
    }
  };

  // ===================
  // Desktop Tab Button
  // ===================
  const TabButton = ({ id, label }) => (
    <button
      onClick={() => {
        setActiveTab(id);
      }}
      className={`px-5 py-2 text-sm md:text-base font-semibold border-b-4 transition-all duration-300 ${
        activeTab === id
          ? "border-blue-600 text-blue-700 bg-blue-50"
          : "border-transparent text-gray-600 hover:text-blue-600 hover:bg-gray-50"
      }`}
    >
      {label}
    </button>
  );

  // ===================
  // Main Component
  // ===================
  return (
    <div className="flex min-h-screen bg-gray-50 font-sans">
      {/* Sidebar - Desktop */}
      <div className="hidden lg:block">
        <DashboardSidebar
          activeSection={activeSection}
          setActiveSection={setActiveSection}
        />
      </div>

      {/* Sidebar - Mobile */}
      <div
        className={`fixed inset-y-0 left-0 bg-white w-72 shadow-2xl transform transition-transform duration-300 ease-in-out z-50 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } lg:hidden`}
      >
        <DashboardSidebar
          activeSection={activeSection}
          setActiveSection={setActiveSection}
        />
        <button
          className="absolute top-4 right-4 text-gray-500 hover:text-blue-600"
          onClick={() => setSidebarOpen(false)}
        >
          <X size={28} />
        </button>
      </div>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      {/* Main Content */}
      <main className="flex-1 flex flex-col w-full">
        {/* Header */}
        <div className="sticky top-0 bg-white z-30 shadow-sm flex items-center justify-between px-4 sm:px-8 py-4">
          <button
            className="lg:hidden text-gray-700 hover:text-blue-600"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu size={28} />
          </button>

          <div className="flex-1 flex justify-center">
            <DashboardHeader
              title="Contact Us Management"
              backTo="/dashboard"
            />
          </div>
        </div>

        {/* Tabs Navigation with responsive behavior */}
        <div className="bg-white border-b border-gray-200 shadow-sm">
          {/* Mobile & Tablet: Hamburger-style selector */}
          <div className="px-4 py-2 flex items-center justify-between lg:hidden">
            <span className="font-semibold text-gray-800 text-sm">
              {activeTabLabel}
            </span>
            <button
              onClick={() => setTabMenuOpen((prev) => !prev)}
              className="flex items-center gap-2 px-3 py-2 text-sm border rounded-lg text-gray-700 hover:bg-gray-50"
            >
              <span>Sections</span>
              <Menu size={18} />
            </button>
          </div>

          {tabMenuOpen && (
            <div className="px-4 pb-3 space-y-1 lg:hidden">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setTabMenuOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium ${
                    activeTab === tab.id
                      ? "bg-blue-50 text-blue-700"
                      : "text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}

          {/* Desktop: Horizontal tabs */}
          <div className="hidden lg:flex justify-center gap-2 sm:gap-4 px-2 sm:px-4 py-2 flex-wrap">
            {tabs.map((tab) => (
              <TabButton key={tab.id} id={tab.id} label={tab.label} />
            ))}
          </div>
        </div>

        {/* Active Form Section */}
        <div className="p-4 sm:p-8 md:p-10 max-w-6xl mx-auto w-full bg-white mt-4 sm:mt-6 rounded-xl shadow-md">
          {renderActiveTab()}
        </div>
      </main>
    </div>
  );
};

export default ContactUs;
